import React, { useState, useEffect } from 'react';
import { ChevronRight, Loader2, Share2 } from 'lucide-react';
import { xuanxueStoriesData, XuanxueStory } from '../data/xuanxueStories';
import { MarkdownRenderer } from './MarkdownContent';
import { ReaderSettingsMenu, useReaderDisplaySettings } from './ReaderDisplaySettings';
import XuanYuanTopBar from './XuanYuanTopBar';

interface StoryReaderProps {
  onBack: () => void;
}

const stripFrontmatter = (raw: string): string => {
  if (raw.startsWith('---')) {
    const end = raw.indexOf('---', 3);
    if (end !== -1) {
      return raw.slice(end + 3).trim();
    }
  }
  return raw.trim();
};

const stripDuplicateStoryHeading = (raw: string, title: string): string => {
  const normalizedTitle = String(title || '').trim();
  if (!normalizedTitle) return raw;
  const lines = String(raw || '').split('\n');
  if (lines.length === 0) return raw;
  const first = String(lines[0] || '').trim();
  const m = first.match(/^#{1,2}\s+(.+)$/);
  if (!m) return raw;
  const heading = String(m[1] || '').trim();
  if (heading !== normalizedTitle) return raw;
  const rest = lines.slice(1);
  while (rest.length > 0 && String(rest[0] || '').trim() === '') rest.shift();
  return rest.join('\n').trim();
};

const StoryReader: React.FC<StoryReaderProps> = ({ onBack }) => {
  const [selectedStory, setSelectedStory] = useState<XuanxueStory | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareNotice, setShareNotice] = useState('');
  const { settings, update, reset } = useReaderDisplaySettings('story-reader', {
    fontSize: 13,
    paragraphSpacing: 14,
  });

  const handleShareCatalog = async () => {
    const url = new URL('/stories', window.location.origin).toString();
    try {
      if (navigator.share) {
        await navigator.share({ title: '轩辕国学 · 玄学故事', url });
        return;
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareNotice('链接已复制，可直接分享。');
        window.setTimeout(() => setShareNotice(''), 2200);
        return;
      }
      setShareNotice(url);
      window.setTimeout(() => setShareNotice(''), 3200);
    } catch {
      setShareNotice('分享失败，请稍后重试。');
      window.setTimeout(() => setShareNotice(''), 2200);
    }
  };

  useEffect(() => {
    if (!selectedStory) return;
    setLoading(true);
    setError(null);
    fetch(selectedStory.file)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('加载失败'))))
      .then((text) => {
        const cleaned = stripFrontmatter(text);
        setContent(stripDuplicateStoryHeading(cleaned, selectedStory.title));
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || '加载失败');
        setLoading(false);
      });
  }, [selectedStory]);

  if (selectedStory) {
    return (
      <div className="min-h-screen bg-white flex flex-col animate-fade-in">
        <XuanYuanTopBar
          title="轩辕国学·玄学故事"
          onBack={() => { setSelectedStory(null); setContent(''); }}
          variant="light"
          right={(
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          )}
        />
        <div className="max-w-3xl mx-auto w-full p-4 sm:p-6 md:p-10 pb-16 sm:pb-20">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
            </div>
          )}
          {error && (
            <div className="py-12 text-center text-slate-500">{error}</div>
          )}
          {!loading && !error && content && (
            <>
              <MarkdownRenderer content={content} settings={settings} />
              <div className="mt-12 pt-8 border-t border-slate-100 text-center">
                <button
                  onClick={() => { setSelectedStory(null); setContent(''); }}
                  className="px-8 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full font-bold transition-colors"
                >
                  返回目录
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·玄学故事"
        onBack={onBack}
        subtitle={shareNotice || '若轩老师的奇门玄学故事'}
        variant="light"
        right={(
          <div className="flex items-center gap-2">
            <button
              onClick={() => { void handleShareCatalog(); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
              aria-label="分享故事目录"
            >
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">分享</span>
            </button>
            <ReaderSettingsMenu
              settings={settings}
              onChange={update}
              onReset={reset}
              buttonClassName="border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
            />
          </div>
        )}
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 text-center lg:text-left">
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 font-semibold">真实案例</span>
              <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold">奇门视角</span>
              <span className="text-slate-600">按故事场景拆解关键断点</span>
            </div>
          </div>

          <div className="grid gap-2 md:gap-3 md:grid-cols-2 lg:grid-cols-2">
            {xuanxueStoriesData.map((story, idx) => (
              <button
                key={story.id}
                onClick={() => setSelectedStory(story)}
                className="group relative flex flex-col text-left bg-white p-2.5 sm:p-3.5 min-h-[84px] sm:min-h-[96px] rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-indigo-300 transition-all overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-20 h-20 -mr-6 -mt-6 rounded-full opacity-10 transition-transform group-hover:scale-150 bg-amber-500"></div>
                <div className="flex items-start justify-between gap-2 mb-1.5 relative z-10">
                  <h3 className="text-[15px] sm:text-base font-medium text-slate-800 group-hover:text-indigo-700 transition-colors relative z-10 line-clamp-1">
                    {idx + 1}. {story.title}
                  </h3>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="text-xs sm:text-[13px] text-slate-500 leading-snug relative z-10 line-clamp-2">
                  点击阅读全文
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryReader;
